
import {
  createSkill,
  deleteSkill,
  listSkills,
  updateSkill,
  type Skill,
} from "../api/settings.js";

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function skillView(skill: Skill): { rawName: string; name: string; meta: string; valid: boolean; error: string } {
  return {
    rawName: skill.name,
    name: escapeHtml(skill.name),
    meta: escapeHtml(skill.valid ? `${skill.description} — ${skill.path}` : skill.path),
    valid: skill.valid,
    error: escapeHtml(skill.error ?? ""),
  };
}

async function submitSkill(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  await createSkill({
    name: String(data.get("name") ?? "").trim(),
    description: String(data.get("description") ?? "").trim(),
    content: String(data.get("content") ?? ""),
  });
}

async function submitSkillEdit(form: HTMLFormElement, name: string): Promise<void> {
  const data = new FormData(form);
  await updateSkill(name, {
    description: String(data.get("description") ?? "").trim(),
    content: String(data.get("content") ?? ""),
  });
}

const styles = css`
  .account-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .account {
    align-items: center;
    border: 1px solid var(--border);
    border-radius: 10px;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    padding: 12px 14px;
  }

  .name {
    color: var(--foreground);
    font-size: 14px;
    font-weight: 600;
  }

  .meta,
  .muted {
    color: var(--muted-foreground);
    font-size: 13px;
    line-height: 1.5;
  }

  .row-actions {
    display: flex;
    flex: 0 0 auto;
    gap: 8px;
  }

  form {
    display: grid;
    gap: 14px;
  }

  label {
    color: var(--foreground);
    display: grid;
    font-size: 13px;
    font-weight: 500;
    gap: 6px;
  }

  input,
  textarea {
    background: var(--background);
    border: 1px solid var(--input);
    border-radius: 8px;
    box-sizing: border-box;
    color: var(--foreground);
    font: inherit;
    font-size: 14px;
    outline: none;
    padding: 8px 10px;
    width: 100%;
  }

  input {
    height: 36px;
  }

  textarea {
    min-height: 200px;
    resize: vertical;
  }

  input:focus,
  textarea:focus {
    border-color: var(--ring);
    box-shadow: 0 0 0 3px var(--ring-shadow);
  }

  .skill-content textarea {
    font-family:
      ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
  }

  .actions,
  .row-actions {
    display: flex;
    gap: 8px;
  }

  .actions app-button,
  .row-actions app-button {
    width: auto;
  }

  .error {
    color: var(--destructive);
    font-size: 13px;
    margin: 8px 0 0;
  }

  .error:empty {
    display: none;
  }

  .invalid {
    color: var(--destructive);
    font-size: 13px;
    margin-top: 4px;
  }
`;

export class AppSettingsSkills extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSettingsSkills)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = [];

  #skills;
  #loaded;
  #error;
  #editingName;
  #editingDescription;
  #editingContent;
  
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #eventContexts = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  #setSkills(value) {
    if (Object.is(this.#skills, value)) return;
    this.#skills = value;
    this.#updateBindings('skills');
  }

  #setLoaded(value) {
    if (Object.is(this.#loaded, value)) return;
    this.#loaded = value;
    this.#updateBindings('loaded');
  }

  #setError(value) {
    if (Object.is(this.#error, value)) return;
    this.#error = value;
    this.#updateBindings('error');
  }

  #setEditingName(value) {
    if (Object.is(this.#editingName, value)) return;
    this.#editingName = value;
    this.#updateBindings('editingName');
  }

  #setEditingDescription(value) {
    if (Object.is(this.#editingDescription, value)) return;
    this.#editingDescription = value;
    this.#updateBindings('editingDescription');
  }

  #setEditingContent(value) {
    if (Object.is(this.#editingContent, value)) return;
    this.#editingContent = value;
    this.#updateBindings('editingContent');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;

  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;

      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    void listSkills()
        .then((items) => {
        this.#setSkills(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to load skills.");
    });
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }


  #initState() {
    this.#skills = [];
    this.#loaded = false;
    this.#error = "";
    this.#editingName = "";
    this.#editingDescription = "";
    this.#editingContent = "";
  }

  #render() {
    const skillViews = this.#skills.map(skillView);
    return `<style>${this.#escText(styles)}</style><app-settings-section title="Skills" description="Skills are reusable instruction sets your agents load on demand. Built-in skills are always available and are not listed here."><!--argon-text:0-->${(skillViews.length > 0) ? `<div class="account-list">${skillViews.map((skill, __argonIndex) => this.#mapEventItem(`<div class="account"><div><div class="name">${skill.name}</div><div class="meta">${skill.meta}</div>${skill.valid ? this.#escText("") : `<div class="invalid">${skill.error}</div>`}</div><div class="row-actions">${skill.valid ? `<app-button variant="outline" size="sm" data-argon="4463">Edit</app-button>` : this.#escText("")}<app-button variant="outline" size="sm" data-argon="5146">Remove</app-button></div></div>`, "initial:1:5146.4463:" + __argonIndex, { 5146: () => {
    if (!window.confirm(`Remove ${skill.rawName} and every script, reference, and asset in its bundle?`))
        return;
    void deleteSkill(skill.rawName)
        .then(() => listSkills())
        .then((items) => {
        if (this.#editingName === skill.rawName) {
            this.#setEditingName("");
            this.#setEditingDescription("");
            this.#setEditingContent("");
        }
        this.#setSkills(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove skill.");
    });
}, 4463: () => {
    const found = this.#skills.find((item) => item.name === skill.rawName);
    if (!found)
        return;
    this.#setError("");
    this.#setEditingName(found.name);
    this.#setEditingDescription(found.description);
    this.#setEditingContent(found.content);
} }, ["5146", "4463"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No skills yet." : "Loading skills...")}</p>`}<!--/argon-text:0--></app-settings-section><!--argon-text:1-->${this.#editingName ? `<app-settings-section title="Edit skill" description="Update the standard description or Markdown instructions. The skill name cannot be changed."><form data-argon="6542"><label>Description<input name="description" required value="${this.#escAttr(this.#editingDescription)}" autocomplete="off"></label><label class="skill-content">Content<textarea name="content" required>${this.#escText(this.#editingContent)}</textarea></label><div class="actions"><app-button type="submit">Save changes</app-button><app-button variant="outline" data-argon="7697">Cancel</app-button></div><p class="error">${this.#escText(this.#error)}</p></form></app-settings-section>` : `<app-settings-section title="Add skill" description="The name is a unique slug, e.g. python-debugging. Content is Markdown instructions the agent follows when this skill is active."><form data-argon="8330"><label>Name<input name="name" required placeholder="python-debugging" autocomplete="off" pattern="[a-z0-9](?:(?:[a-z0-9]|-(?!-)){0,62}[a-z0-9])?"></label><label>Description<input name="description" required placeholder="One or two sentence summary used for search." autocomplete="off"></label><label class="skill-content">Content<textarea name="content" required placeholder="Markdown instructions, context, or steps the agent should follow."></textarea></label><div class="actions"><app-button type="submit">Add skill</app-button></div><p class="error">${this.#escText(this.#error)}</p></form></app-settings-section>`}<!--/argon-text:1-->`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      this.#eventHandler(target, id, handler).call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #mapEventItem(html, ctx, handlers, ids) {
    const key = String(ctx);
    this.#eventContexts[key] = handlers;
    const attr = key.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
    let out = String(html);
    for (const id of ids) out = out.replaceAll(`data-argon="${id}"`, `data-argon="${id}" data-argon-c="${attr}"`);
    return out;
  }

  #eventHandler(el, id, fallback) {
    const ctx = el.getAttribute('data-argon-c');
    return ctx && this.#eventContexts[ctx]?.[id] ? this.#eventContexts[ctx][id] : fallback;
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const skillViews = this.#skills.map(skillView);
    if (prop === undefined || prop === 'skills' || prop === 'loaded') {
      const __argonNext = `${(skillViews.length > 0) ? `<div class="account-list">${skillViews.map((skill, __argonIndex) => this.#mapEventItem(`<div class="account"><div><div class="name">${skill.name}</div><div class="meta">${skill.meta}</div>${skill.valid ? this.#escText("") : `<div class="invalid">${skill.error}</div>`}</div><div class="row-actions">${skill.valid ? `<app-button variant="outline" size="sm" data-argon="4463">Edit</app-button>` : this.#escText("")}<app-button variant="outline" size="sm" data-argon="5146">Remove</app-button></div></div>`, "0:1:5146.4463:" + __argonIndex, { 5146: () => {
    if (!window.confirm(`Remove ${skill.rawName} and every script, reference, and asset in its bundle?`))
        return;
    void deleteSkill(skill.rawName)
        .then(() => listSkills())
        .then((items) => {
        if (this.#editingName === skill.rawName) {
            this.#setEditingName("");
            this.#setEditingDescription("");
            this.#setEditingContent("");
        }
        this.#setSkills(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove skill.");
    });
}, 4463: () => {
    const found = this.#skills.find((item) => item.name === skill.rawName);
    if (!found)
        return;
    this.#setError("");
    this.#setEditingName(found.name);
    this.#setEditingDescription(found.description);
    this.#setEditingContent(found.content);
} }, ["5146", "4463"])).join("")}</div>` : `<p class="muted">${this.#escText(this.#loaded ? "No skills yet." : "Loading skills...")}</p>`}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'editingName' || prop === 'editingDescription' || prop === 'editingContent' || prop === 'error') {
      const __argonNext = `${this.#editingName ? `<app-settings-section title="Edit skill" description="Update the standard description or Markdown instructions. The skill name cannot be changed."><form data-argon="6542"><label>Description<input name="description" required value="${this.#escAttr(this.#editingDescription)}" autocomplete="off"></label><label class="skill-content">Content<textarea name="content" required>${this.#escText(this.#editingContent)}</textarea></label><div class="actions"><app-button type="submit">Save changes</app-button><app-button variant="outline" data-argon="7697">Cancel</app-button></div><p class="error">${this.#escText(this.#error)}</p></form></app-settings-section>` : `<app-settings-section title="Add skill" description="The name is a unique slug, e.g. python-debugging. Content is Markdown instructions the agent follows when this skill is active."><form data-argon="8330"><label>Name<input name="name" required placeholder="python-debugging" autocomplete="off" pattern="[a-z0-9](?:(?:[a-z0-9]|-(?!-)){0,62}[a-z0-9])?"></label><label>Description<input name="description" required placeholder="One or two sentence summary used for search." autocomplete="off"></label><label class="skill-content">Content<textarea name="content" required placeholder="Markdown instructions, context, or steps the agent should follow."></textarea></label><div class="actions"><app-button type="submit">Add skill</app-button></div><p class="error">${this.#escText(this.#error)}</p></form></app-settings-section>`}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '5146').forEach(el => this.#listen(el, '5146', 'click', () => {
    if (!window.confirm(`Remove ${skill.rawName} and every script, reference, and asset in its bundle?`))
        return;
    void deleteSkill(skill.rawName)
        .then(() => listSkills())
        .then((items) => {
        if (this.#editingName === skill.rawName) {
            this.#setEditingName("");
            this.#setEditingDescription("");
            this.#setEditingContent("");
        }
        this.#setSkills(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch(() => {
        this.#setError("Failed to remove skill.");
    });
}));
    this.#eventTargets(scope, '4463').forEach(el => this.#listen(el, '4463', 'click', () => {
    const found = this.#skills.find((item) => item.name === skill.rawName);
    if (!found)
        return;
    this.#setError("");
    this.#setEditingName(found.name);
    this.#setEditingDescription(found.description);
    this.#setEditingContent(found.content);
}));
    this.#eventTargets(scope, '6542').forEach(el => this.#listen(el, '6542', 'submit', (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    this.#setError("");
    void submitSkillEdit(form, this.#editingName)
        .then(() => listSkills())
        .then((items) => {
        this.#setEditingName("");
        this.#setEditingDescription("");
        this.#setEditingContent("");
        this.#setSkills(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch((err) => {
        this.#setError(err instanceof Error ? err.message : "Failed to update skill.");
    });
}));
    this.#eventTargets(scope, '7697').forEach(el => this.#listen(el, '7697', 'click', () => {
    this.#setError("");
    this.#setEditingName("");
    this.#setEditingDescription("");
    this.#setEditingContent("");
}));
    this.#eventTargets(scope, '8330').forEach(el => this.#listen(el, '8330', 'submit', (event: Event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    this.#setError("");
    void submitSkill(form)
        .then(() => {
        form.reset();
        return listSkills();
    })
        .then((items) => {
        this.#setSkills(items);
        this.#setLoaded(true);
        this.#setError("");
    })
        .catch((err) => {
        this.#setError(err instanceof Error ? err.message : "Failed to add skill.");
    });
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
